/**
 * Users module (Add User / User Authentication) — dirty-gate (ACME-1050).
 *
 * ## Standing product rule
 * Unsaved / leave: no real change → navigate silently. Prompt only when dirty.
 * Edit-style screens: Save stays disabled until dirty. Delete always prompts.
 *
 * ## Why only two call sites
 * `UserInfo` (Edit User → General Information) already tracks dirty correctly via
 * `hasUnsavedChanges` + `hasAnyFieldChanged()` against `originalFormData`, and its Save
 * is already gated on it. Do not port that screen onto this util — it works, and swapping
 * it risks its Save button. This util exists for the two forms that had **no** dirty
 * signal at all: `AddNewUserForm` and `UserAuthentication` (the latter declared
 * `hasUnsavedChanges` but nothing ever assigned it).
 *
 * Correct pattern:
 * 1. Capture `captureUserFormBaseline(this.formData)` **before the first `await`** in
 *    `connectedCallback`. The form is already rendered and typable while those lookups
 *    run, and a null baseline reads as clean, so a late capture silently swallows
 *    whatever the user typed during the load.
 * 2. When the load writes a default the user never chose, fold it into the baseline with
 *    `applyUserFormBaselineDefaults` instead of re-snapshotting live `formData` — that
 *    keeps the default from reading as dirty without also absorbing the user's edits.
 * 3. On Cancel, if `!isUserFormDirty(baseline, this.formData)` → navigate, no dialog.
 * 4. Otherwise `openJhftDialog({ variant: "warning", ... })`.
 *
 * ## Assumption
 * Snapshots every key on `formData`. Both call sites keep `formData` small and fully
 * user-editable (Add User: 10 text/select fields; Authentication: userState/authType/userType),
 * so there is nothing to filter out. If a screen later stores ephemeral UI state on
 * `formData`, snapshot an explicit field list instead of widening this.
 *
 * ## Unsafe changes
 * - Re-snapshotting live `formData` after an await (`_cancelBaseline = capture(this.formData)`
 *   at the end of `connectedCallback`). It cannot tell a code-written default from a user
 *   edit, so it treats both as the starting point and Cancel then leaves without a prompt.
 *   Fold defaults in instead.
 * - Leaving the baseline null until the load resolves: an await that is slow (or throws)
 *   leaves the form dirty-blind, and on Authentication that also pins Save disabled.
 * - Treating "field has any value" as dirty — that is the Edit-screen false positive this
 *   whole ticket exists to remove.
 */

function stable(value: unknown): string {
  if (value == null) return "";
  if (typeof value !== "object") return String(value).trim();
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stable(obj[k])}`)
    .join(",")}}`;
}

function normalize(formData: Record<string, unknown> | null | undefined): Record<string, string> {
  const src = formData ?? {};
  const out: Record<string, string> = {};
  for (const key of Object.keys(src).sort()) {
    out[key] = stable(src[key]);
  }
  return out;
}

/** Stable JSON of user-meaningful form state for Cancel / Save compare. */
export function captureUserFormBaseline(formData: Record<string, unknown> | null | undefined): string {
  return JSON.stringify(normalize(formData));
}

/**
 * Fold values the load wrote itself into an existing baseline, so they do not read as
 * user changes while everything the user typed in the meantime still does.
 *
 * @param defaults the fields the code just assigned (e.g. an auto-filled business unit)
 */
export function applyUserFormBaselineDefaults(
  baseline: string | null | undefined,
  defaults: Record<string, unknown> | null | undefined,
): string {
  let captured: Record<string, string> = {};
  if (baseline) {
    try {
      captured = JSON.parse(baseline) as Record<string, string>;
    } catch {
      captured = {};
    }
  }
  return captureUserFormBaseline({ ...captured, ...(defaults ?? {}) });
}

/** @returns false when baseline missing (treat as clean — leave silently). */
export function isUserFormDirty(
  baseline: string | null | undefined,
  formData: Record<string, unknown> | null | undefined,
): boolean {
  if (!baseline) return false;
  return captureUserFormBaseline(formData) !== baseline;
}
